<template>
  <Card
    :is-editable="isEditable"
    :page-reference="pageReference"
    :name="kpiName"
    :metric="bigMetric"
    :is-big-kpi="true"
    :edit-chart-k-p-i-callback="toggleMetricEdit">
    <div class="relative flex justify-between w-[full] h-full min-h-full overflow-auto">
      <div v-for="metric in metrics" :key="metric.id" class="flex justify-between ml-[16px] mr-[16px] relative my-3">
        <div class="flex flex-col translate-y-[0px]">
          <div class="text-[14px] text-common-grey">
            {{ isMocked ? metric.label : metric.name }}
          </div>
          <div class="mt-1 flex items-center content-center gap 1">
            <div
              v-if="!Object.keys(metric).includes('edit') || (Object.keys(metric).includes('edit') && !metricEdit)"
              class="text-[24px] flex max-w[30x] font-bold text-grey-light-body dark:text-dark-body-text">
              <span v-if="symbolPre?.length">{{ symbolPre }}</span>
              <span v-if="!(symbolPre || symbolPost)">{{ metric?.value }}</span>
              <span v-if="symbolPre || symbolPost">{{
                $utils.truncateNumber(
                  Number(metric?.value?.toString().replaceAll(",", "")?.replace("$", "")?.replace("%", "").replace("-", ""))
                )
              }}</span>
              <span v-if="symbolPost?.length">{{ symbolPost }}</span>
            </div>
            <div
              v-if="!Object.keys(metric).includes('edit') || (Object.keys(metric).includes('edit') && !metricEdit)"
              class="ml-1 p-1 flex items-center content-center rounded-full"
              :class="
                metric?.value
                  ? metric?.value?.toString().includes('-')
                    ? '!bg-status-error-red-transparent'
                    : '!bg-status-success-green-transparent'
                  : ''
              ">
              <ChevronDown
                v-if="metric?.value && metric?.value?.toString().includes('-')"
                class="h-3 w-3"
                :class="
                  metric?.value
                    ? metric?.value.toString().includes('-')
                      ? '!text-status-error-red'
                      : '!text-status-success-green'
                    : '!text-status-warning-yellow'
                " />
              <ChevronUp
                v-if="metric?.value && !metric?.value?.toString().includes('-')"
                class="h-3 w-3"
                :class="
                  metric?.value
                    ? metric?.value.toString().includes('-')
                      ? '!text-status-error-red'
                      : '!text-status-success-green'
                    : '!text-status-warning-yellow'
                " />
              <div
                v-if="metric?.value"
                class="text-sm pr-1 text-center flex"
                :class="
                  metric?.value
                    ? metric?.value?.toString().includes('-')
                      ? '!text-status-error-red'
                      : '!text-status-success-green'
                    : '!text-status-warning-yellow'
                ">
                <span v-if="symbolPre?.length">{{ symbolPre }}</span>
                <span v-if="metric?.value?.toString().includes('$')">$</span>
                {{
                  $utils.truncateNumber(
                    Number(metric?.value?.toString().replaceAll(",", "")?.replace("$", "")?.replace("%", "").replace("-", ""))
                  )
                }}
                <!--                {{ $utils.truncateNumber($utils.slugify(metric.value)) }}-->
                <span v-if="metric?.value?.toString().includes('%')">%</span>
                <span v-if="symbolPost?.length">{{ symbolPost }}</span>
              </div>
              <div v-else-if="metric?.value != 0" class="-ml-2 text-common-grey w-32 flex flex-nowrap">
                <span>{{ $t("No available data") }}</span>
              </div>
            </div>
            <div
              v-if="Object.keys(metric).includes('edit') && metricEdit && is_range_target_edit"
              class="mt-[5px] flex flex-row justify-start h-[25px] items-center">
              <input
                :disabled="editing_disabled"
                :value="metric.value"
                type="number"
                min="0"
                class="w-[100px] h-[25px] appearance-none block mt-1 px-3 py-2 border border-light-divider-border dark:border-dark-divider-border rounded-md shadow-sm dark:bg-dark-box-background placeholder-common-grey text-light-body-text dark:text-dark-body-text focus:outline-none focus:ring-0 dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue text-sm transition ease-in-out duration-200 min-h-[42px]"
                placeholder="Target"
                @input.prevent=""
                @keyup.esc="toggleMetricEdit"
                @keyup.enter="toggleMetricEdit"
                @focusout="(event) => handleChange(event, metric)">
              <Check
                class="hover:bg-gray-100 dark:hover:bg-dark-screen-background text-gray-500 h-10 w-10 block z-40 transition rounded-md duration-500 ease-in-out p-2"
                aria-hidden="true"
                @click="toggleMetricEdit" />
              <span class="whitespace-nowrap text-sm mx-1 font-thin italic">
                {{ selectedRange?.start.format("MM/DD/YY") }} - {{ selectedRange?.end.format("MM/DD/YY") }}
              </span>
            </div>
            <div
              v-else-if="Object.keys(metric).includes('edit') && metricEdit"
              class="mt-[5px] flex flex-row justify-start h-[25px] items-center">
              <input
                :disabled="editing_disabled"
                :value="metric.value"
                type="number"
                min="0"
                class="w-[100px] h-[25px] appearance-none block mt-1 px-3 py-2 border border-light-divider-border dark:border-dark-divider-border rounded-md shadow-sm dark:bg-dark-box-background placeholder-common-grey text-light-body-text dark:text-dark-body-text focus:outline-none focus:ring-0 dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue text-sm transition ease-in-out duration-200 min-h-[42px]"
                placeholder="Target"
                @input.prevent=""
                @keyup.esc="toggleMetricEdit"
                @keyup.enter="toggleMetricEdit"
                @focusout="(event) => handleChange(event, metric)">
              <Check
                class="hover:bg-gray-100 dark:hover:bg-dark-screen-background text-gray-500 h-10 w-10 block z-40 transition rounded-md duration-500 ease-in-out p-2"
                aria-hidden="true"
                @click="toggleMetricEdit" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import Card from "./Card.vue"
import { ChevronDown, ChevronUp, Check } from "lucide-vue-next"
import dataService from "@/services/data.service"

export default {
  components: {
    Card,
    ChevronUp,
    Check,
    ChevronDown
  },

  inject: ["canUserEditLayout"],
  props: ["metrics", "bigMetric", "isMocked", "kpiName", "isEditable", "symbolPost", "symbolPre", "pageReference", "selectedRange"],
  emits: ["done"],
  data() {
    return {
      metricEdit: false,
      is_range_target_edit: false
    }
  },

  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },
  methods: {
    toggleMetricEdit(type) {
      if (type == "range-target") {
        this.is_range_target_edit = true
      }
      this.metricEdit = !this.metricEdit
    },
    updateForecastScale(item, newValue) {
      let data = {}
      if (this.is_range_target_edit) {
        dataService
          .updateForecast(this.$store.state.data.current_forecast.id, {
            range_goals: this.$store.state.data.current_forecast.range_goals.concat([
              {
                name: item["name"],
                goal: Number(newValue),
                from_date: this.$moment(this.selectedRange?.start).format("YYYY-MM-DDTHH:mm:ss"),
                to_date: this.$moment(this.selectedRange?.end).format("YYYY-MM-DDTHH:mm:ss")
              }
            ])
          })
          .then((result) => {
            item["value"] = result.data[item["name"]]
            this.metricEdit = false
          })
          .then(() => {
            this.$emit("done", true)
          })
      } else {
        data[item["name"]] = Number(newValue)
        dataService
          .updateForecast(this.$store.state.data.current_forecast.id, data)
          .then((result) => {
            item["value"] = result.data[item["name"]]
            this.metricEdit = false
          })
          .then(() => {
            this.$emit("done")
          })
      }
    },
    handleChange(event, metric) {
      if (event.currentTarget.value > 0) {
        this.$store.dispatch("ui/showLoading")
        setTimeout(() => {
          this.$store.dispatch("ui/hideLoading")
        }, 100)
        this.updateForecastScale(metric, event.currentTarget.value)
      }
    }
  }
}
</script>
